import React, { useState } from 'react';
import { Gift, Mail, User, MessageSquare, CreditCard, Check, ArrowRight, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import axios from 'axios';
import { API } from '../config';
import { trackPixel } from '../lib/pixel';

const VOUCHER_SHARE_MSG = `Give the gift of relaxation! 💆 Dublin Holistic Massage offers digital gift vouchers (€25–€500) — delivered instantly by email, valid for 12 months and redeemable for any massage treatment.\n\nGet one here: https://www.dublinholisticmassages.com`;

const GiftVoucherSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({
    purchaser_name: '',
    purchaser_email: '',
    recipient_name: '',
    recipient_email: '',
    message: ''
  });

  const presetAmounts = [50, 75, 100, 125, 150, 200];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmount = (value) => {
    const num = parseInt(value);
    if (!isNaN(num) && num >= 25 && num <= 500) {
      setSelectedAmount(num);
    }
    setCustomAmount(value);
  };

  const [error, setError] = useState('');

  const handlePurchase = async () => {
    if (!isFormValid()) return;
    setLoading(true);
    setError('');
    
    const whatsappFallback = () => {
      const msg = encodeURIComponent(
        `Hi, I'd like to purchase a €${selectedAmount} gift voucher.\nFor: ${formData.recipient_name}\nFrom: ${formData.purchaser_name}\n${formData.message ? `Message: ${formData.message}` : ''}`
      );
      window.open(`https://wa.me/353857747474?text=${msg}`, '_blank');
    };

    try {
      // Step 1: Create voucher
      const createResponse = await axios.post(`${API}/vouchers/create`, {
        amount: selectedAmount,
        ...formData
      });

      const voucherId = createResponse.data.voucher_id;

      trackPixel('InitiateCheckout', {
        value: selectedAmount,
        currency: 'EUR',
        content_name: 'Gift Voucher',
        content_category: 'gift_voucher',
      });

      // Step 2: Create checkout
      try {
        const checkoutResponse = await axios.post(`${API}/vouchers/${voucherId}/checkout`, null, {
          params: { return_url: window.location.origin + '/voucher-confirmed' }
        });

        // Redirect to payment
        window.location.href = checkoutResponse.data.checkout_url;
      } catch (checkoutError) {
        setError('Payment setup failed. You can complete your purchase via WhatsApp.');
        setLoading(false);
      }
    } catch (error) {
      setError('Unable to process your request. You can complete your purchase via WhatsApp.');
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.purchaser_name &&
      formData.purchaser_email &&
      formData.recipient_name &&
      formData.recipient_email &&
      selectedAmount >= 25
    );
  };

  return (
    <>
      {/* Gift Voucher Section on Homepage */}
      <section id="gift-vouchers" className="py-28 bg-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-sage-200/30 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">Perfect Gift</span>
              
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-luxury-green mt-4 mb-6">
                Give the Gift of
                <span className="block italic text-luxury-gold mt-1">Relaxation</span>
              </h2>
              
              <p className="text-stone-500 font-light mb-6 leading-relaxed">
                Treat someone special to a rejuvenating massage experience. Our digital gift vouchers 
                are delivered instantly via email.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-stone-700">
                  <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center">
                    <Check size={14} className="text-sage-600" />
                  </div>
                  <span>Instant email delivery</span>
                </li>
                <li className="flex items-center gap-3 text-stone-700">
                  <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center">
                    <Check size={14} className="text-sage-600" />
                  </div>
                  <span>Valid for 12 months</span>
                </li>
                <li className="flex items-center gap-3 text-stone-700">
                  <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center">
                    <Check size={14} className="text-sage-600" />
                  </div>
                  <span>Redeemable for any service</span>
                </li>
                <li className="flex items-center gap-3 text-stone-700">
                  <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center">
                    <Check size={14} className="text-sage-600" />
                  </div>
                  <span>Add a personal message</span>
                </li>
              </ul>

              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/30 group"
                  data-testid="buy-voucher-btn"
                >
                  <Gift size={20} className="mr-2" />
                  Buy Gift Voucher
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(VOUCHER_SHARE_MSG)}`, '_blank')}
                  className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full w-12 h-12 p-0 flex items-center justify-center flex-shrink-0"
                  data-testid="voucher-whatsapp-share"
                  title="Share gift vouchers on WhatsApp"
                >
                  <MessageCircle size={20} />
                </Button>
              </div>
            </div>

            {/* Voucher Preview Card */}
            <div className="relative">
              <div className="bg-gradient-to-br from-sage-600 to-sage-700 rounded-3xl p-8 text-white shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Sparkles size={24} />
                    <span className="font-semibold">Gift Voucher</span>
                  </div>
                  <span className="text-sage-200 text-sm">Dublin Holistic Massage</span>
                </div>
                
                <div className="text-center py-8">
                  <p className="text-sage-200 mb-2">Value</p>
                  <p className="text-6xl font-bold">€80</p>
                </div>
                
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-sm text-sage-100 mb-1">Voucher Code</p>
                  <p className="font-mono text-xl tracking-wider">DHM-XXXX-XXXX</p>
                </div>
                
                <div className="mt-6 text-center text-sage-200 text-sm">
                  Valid for 12 months from purchase
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center shadow-lg">
                <Gift size={32} className="text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-xl p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <Gift className="text-amber-500" size={24} />
              Purchase Gift Voucher
            </DialogTitle>
            <DialogDescription>
              Step {step} of 2
            </DialogDescription>
          </DialogHeader>

          {/* Progress Bar */}
          <div className="flex gap-2 mb-6">
            <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-amber-500' : 'bg-stone-200'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-amber-500' : 'bg-stone-200'}`} />
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-semibold text-stone-800 mb-3">Select Amount</h3>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {presetAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 px-4 rounded-xl border-2 font-semibold transition-all ${
                        selectedAmount === amount && !customAmount
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-stone-200 hover:border-amber-300 text-stone-700'
                      }`}
                    >
                      €{amount}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">€</span>
                  <Input
                    type="number"
                    placeholder="Custom amount (€25-€500)"
                    value={customAmount}
                    onChange={(e) => handleCustomAmount(e.target.value)}
                    className="pl-8"
                    min={25}
                    max={500}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-base font-semibold text-stone-800">Your Details</h3>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    placeholder="Your name"
                    value={formData.purchaser_name}
                    onChange={(e) => setFormData({ ...formData, purchaser_name: e.target.value })}
                    className="pl-10"
                  />
                </div>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={formData.purchaser_email}
                    onChange={(e) => setFormData({ ...formData, purchaser_email: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!formData.purchaser_name || !formData.purchaser_email}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full py-3"
              >
                Continue
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-stone-800">Recipient Details</h3>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    placeholder="Recipient's name"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    className="pl-10"
                  />
                </div>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    type="email"
                    placeholder="Recipient's email"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-stone-800 mb-2">Personal Message (Optional)</h3>
                <Textarea
                  placeholder="Add a personal message to your gift..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Order Summary */}
              <div className="bg-stone-50 rounded-xl p-4">
                <h3 className="font-semibold text-stone-800 mb-3">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Voucher Amount</span>
                    <span className="font-semibold">€{selectedAmount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">To</span>
                    <span>{formData.recipient_name || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">From</span>
                    <span>{formData.purchaser_name}</span>
                  </div>
                </div>
                <div className="border-t mt-3 pt-3 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="text-xl font-bold text-amber-600">€{selectedAmount}</span>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center" data-testid="voucher-error">
                  <p className="text-red-700 text-sm mb-3">{error}</p>
                  <Button
                    onClick={() => {
                      const msg = encodeURIComponent(
                        `Hi, I'd like to purchase a €${selectedAmount} gift voucher.\nFor: ${formData.recipient_name}\nFrom: ${formData.purchaser_name}\n${formData.message ? `Message: ${formData.message}` : ''}`
                      );
                      window.open(`https://wa.me/353857747474?text=${msg}`, '_blank');
                    }}
                    className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full px-6 py-2 text-sm"
                    data-testid="voucher-whatsapp-fallback-btn"
                  >
                    Complete via WhatsApp
                  </Button>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setStep(1); setError(''); }}
                  className="flex-1 rounded-full"
                >
                  Back
                </Button>
                <Button
                  onClick={handlePurchase}
                  disabled={!isFormValid() || loading}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                >
                  {loading ? 'Processing...' : (
                    <>
                      <CreditCard size={18} className="mr-2" />
                      Pay €{selectedAmount}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GiftVoucherSection;
